"use client"

import { useLayoutEffect, useRef, useState } from "react"

import Image from "next/image"
import { useTranslations } from "next-intl"

import { milliseconds, token } from "@/components/ui/disclosure-motion"
import { IconButton } from "@/components/ui/icon-button"
import { BackChevronIcon } from "@/components/ui/icons/back"
import { InfoIcon } from "@/features/checkout/redesign/Icons"
import { isPhaseArrival, notifyPhaseHeaderMounted } from "@/features/checkout/redesign/phase-transition"
import { clampPhase, phaseState, progressPercent, type OnboardingPhase } from "@/features/checkout/redesign/phases"
import { PhasesSheet } from "@/features/checkout/redesign/PhasesSheet"
import { tv } from "@/lib/utils/tv"

const NAV_CIRCLE = "size-10 border-surface-warm-deep"

// View-transition names: globals.css animates these groups when the picker's overview gives way to the next screen's dots.
const TRACK_NAME = "onboarding-phase-track"
const phaseName = (index: number) => `onboarding-phase-${index}`
const TRACK = "bg-line-faint absolute inset-x-1.75 top-1/2 h-1 -translate-y-1/2 overflow-hidden rounded-full"

const dot = tv({
  base: "relative size-3.5 rounded-full",
  variants: { reached: { true: "bg-brand", false: "bg-line-faint" } },
})

// Entering a phase, its step opens out of its dot: a circle with its icon, its label below. Then globals.css shrinks it back onto the dot.
const step = tv({
  slots: {
    root: "pointer-events-none absolute top-1/2 left-1/2 size-10 -translate-1/2",
    circle: "bg-brand text-fg-inverted flex size-full items-center justify-center rounded-full",
    label: "text-tab text-fg-brand absolute top-full left-1/2 mt-100 -translate-x-1/2 whitespace-nowrap",
  },
  variants: { leaving: { true: { circle: "onboarding-phase-badge", label: "onboarding-phase-label-out" } } },
})

const prefersReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches

const fillTransform = (percent: number) => `translateX(${percent - 100}%)`

interface Shown {
  percent: number
  phase: number
}

// What the last header showed: each screen mounts its own header, and the next one starts from here.
let shown: Shown | null = null
// The phase the last header was on, whatever its bar showed when it left: the overview is before the first, null before any header.
let lastPhase: number | null = null

// The share of the travel's duration at which a cubic-bezier easing reaches `progress`; any other easing is treated as linear.
const timeToReach = (easing: string, progress: number) => {
  const [x1, y1, x2, y2] = easing.match(/[\d.]+/g)?.map(Number) ?? []
  if (x1 === undefined || y1 === undefined || x2 === undefined || y2 === undefined) return progress
  const bezier = (first: number, second: number, at: number) => 3 * first * at * (1 - at) ** 2 + 3 * second * at ** 2 * (1 - at) + at ** 3
  let low = 0
  let high = 1
  for (let step = 0; step < 20; step++) {
    const middle = (low + high) / 2
    if (bezier(y1, y2, middle) < progress) low = middle
    else high = middle
  }
  return bezier(x1, x2, (low + high) / 2)
}

const holdOpen = () => milliseconds(token("--duration-reveal")) ?? 620

// Returns the opening step's animation, which holds open a while before it finishes.
const carryOver = (bar: HTMLElement, from: Shown, to: Shown, opening: boolean): Animation | undefined => {
  const fill = bar.firstElementChild
  if (!(fill instanceof HTMLElement) || typeof fill.animate !== "function" || prefersReducedMotion()) return
  const travel = { duration: milliseconds(token("--duration-reveal")) ?? 620, easing: token("--ease-settle") || "ease-out" }
  if (from.percent !== to.percent) {
    // Retargets from wherever an interrupted travel had got to.
    const start = fill.getAnimations().length > 0 ? getComputedStyle(fill).transform : fillTransform(from.percent)
    fill.animate({ transform: [start, fillTransform(to.percent)] }, travel)
  }
  const reachedColor = getComputedStyle(fill).backgroundColor
  const unreachedColor = getComputedStyle(bar).backgroundColor
  const colorOf = (reached: boolean) => (reached ? reachedColor : unreachedColor)
  const dots = bar.parentElement?.querySelectorAll<HTMLElement>("[data-state]") ?? []
  let opened: Animation | undefined
  dots.forEach((phaseDot, index) => {
    const was = index <= from.phase
    const is = index <= to.phase
    if (was === is) return
    // A dot changes colour as the bar's edge crosses it.
    const crossing = from.percent === to.percent ? 0 : ((index / (dots.length - 1)) * 100 - from.percent) / (to.percent - from.percent)
    const delay = travel.duration * timeToReach(travel.easing, Math.min(Math.max(crossing, 0), 1))
    phaseDot.animate(
      { backgroundColor: [colorOf(was), colorOf(is)] },
      { duration: milliseconds(token("--duration-quick")) ?? 150, easing: travel.easing, delay, fill: "backwards" }
    )
    if (!opening || index !== to.phase) return
    const open = { duration: milliseconds(token("--duration-slow")) ?? 400, easing: token("--ease-settle-soft") || "ease-out", delay, fill: "backwards" as const }
    const duration = open.duration + holdOpen()
    opened = phaseDot.querySelector("[data-step-circle]")?.animate(
      {
        scale: ["0.35", "1", "1"],
        backgroundColor: [unreachedColor, reachedColor, reachedColor],
        offset: [0, open.duration / duration, 1],
        easing: [open.easing, "linear", "linear"],
      },
      { ...open, duration, easing: "linear" }
    )
    phaseDot.querySelector("[data-step-label]")?.animate({ opacity: [0, 1] }, open)
  })
  return opened
}

// Leaving the picker the view transition opens the step; this times the morph and the same hold, so it settles as late as an opened one.
const holdAfterMorph = (bar: HTMLElement): Animation | undefined => {
  const circle = bar.parentElement?.querySelector<HTMLElement>("[data-step-circle]")
  if (!circle || typeof circle.animate !== "function" || prefersReducedMotion()) return
  return circle.animate({ scale: ["1", "1"] }, { duration: (milliseconds(token("--duration-slow")) ?? 400) + holdOpen() })
}

// Leaving mid-travel, the next header starts from what is on screen rather than from this header's destination.
const visiblyShown = (bar: HTMLElement, destination: Shown): Shown => {
  const fill = bar.firstElementChild
  if (!(fill instanceof HTMLElement) || typeof fill.getAnimations !== "function" || fill.getAnimations().length === 0) return destination
  const track = bar.getBoundingClientRect()
  if (track.width === 0) return destination
  const percent = Math.min(Math.max(((fill.getBoundingClientRect().right - track.left) / track.width) * 100, 0), 100)
  const gaps = (bar.parentElement?.querySelectorAll("[data-state]").length ?? 1) - 1
  return { percent, phase: gaps > 0 ? Math.floor((percent / 100) * gaps + 1e-6) : 0 }
}

export interface OnboardingHeaderProps {
  phases: readonly OnboardingPhase[]
  current: number
  fraction?: number
  phasesTitle: string
  overview?: boolean
  onBack?: () => void
  showInfo?: boolean
  showProgress?: boolean
}

export const OnboardingHeader = ({ phases, current, fraction = 0, phasesTitle, overview = false, onBack, showInfo = true, showProgress = true }: OnboardingHeaderProps) => {
  const t = useTranslations("onboarding")
  const [open, setOpen] = useState(false)
  const count = phases.length
  const phase = clampPhase(current, count)
  const active = phases[phase]
  const percent = progressPercent(current, fraction, count)
  // A phase's first screen, one on a later phase than the last header's (or the first header since the page loaded), opens its step
  // as the bar reaches it, holds it, then closes it onto its dot; every other screen shows only the dots. Leaving the picker the view
  // transition opens the step out of the overview's circle instead.
  const [arriving] = useState(() => !overview && (lastPhase === null || phase > lastPhase))
  const [morphing] = useState(isPhaseArrival)
  const [settled, setSettled] = useState(!arriving)
  const [settling, setSettling] = useState(false)
  const played = useRef(false)
  const barRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!overview) notifyPhaseHeaderMounted()
  }, [overview])

  useLayoutEffect(() => {
    if (overview) {
      shown = { percent: 0, phase: -1 }
      lastPhase = -1
      return
    }
    const bar = barRef.current
    if (!bar) return
    // Re-renders, and a strict-mode second run, carry the bar on but never open the step again.
    const opening = arriving && !played.current
    played.current = true
    const destination = { percent, phase }
    const from = shown ?? (opening ? { percent, phase: phase - 1 } : null)
    shown = destination
    lastPhase = phase
    const moved = from && (from.percent !== percent || from.phase !== phase) ? carryOver(bar, from, destination, opening && !morphing) : undefined
    if (opening) {
      const opened = morphing ? holdAfterMorph(bar) : moved
      const settle = () => {
        setSettled(true)
        setSettling(opened !== undefined)
      }
      if (opened) opened.finished.then(settle, () => undefined)
      else settle()
    }
    return () => {
      shown = visiblyShown(bar, destination)
    }
  }, [overview, percent, phase, arriving, morphing])

  return (
    <header className="flex flex-col gap-300 pb-500">
      <div className="flex h-10 items-center justify-between px-700">
        {onBack ? <IconButton aria-label={t("back")} className={NAV_CIRCLE} icon={<BackChevronIcon />} onClick={onBack} /> : <span aria-hidden className="size-10" />}
        <Image src="/images/nelo_logo_web.svg" alt="Nelo" width={68} height={30} />
        {showInfo ? (
          <IconButton aria-label={t("phasesInfo")} aria-haspopup="dialog" className={NAV_CIRCLE} icon={<InfoIcon />} onClick={() => setOpen(true)} />
        ) : (
          <span aria-hidden className="size-10" />
        )}
      </div>
      {active && showProgress && (
        <div className="flex h-10 items-center px-1000">
          <div className="relative flex h-full w-full items-center justify-between">
            {overview ? (
              <>
                <span aria-hidden className={TRACK} style={{ viewTransitionName: TRACK_NAME }} />
                <ol aria-label={phasesTitle} className="relative flex h-full w-full justify-between">
                  {phases.map((item, index) => (
                    <li key={item.id} className="relative w-3.5">
                      <span
                        aria-hidden
                        data-testid="phase-circle"
                        className="bg-surface-page text-fg-heading absolute inset-y-0 left-1/2 flex w-10 -translate-x-1/2 items-center justify-center rounded-full"
                        style={{ viewTransitionName: phaseName(index) }}
                      >
                        {item.icon}
                      </span>
                      <span
                        className="text-tab text-fg-heading absolute top-full left-1/2 mt-100 -translate-x-1/2 whitespace-nowrap"
                        style={{ viewTransitionName: `${phaseName(index)}-label` }}
                      >
                        {item.label}
                      </span>
                    </li>
                  ))}
                </ol>
              </>
            ) : (
              <>
                <div
                  ref={barRef}
                  role="progressbar"
                  aria-label={phasesTitle}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(percent)}
                  aria-valuetext={t("progress", { step: phase + 1, total: count, title: active.title })}
                  className={TRACK}
                  style={{ viewTransitionName: TRACK_NAME }}
                >
                  <div className="bg-brand h-full rounded-full" style={{ transform: fillTransform(percent) }} />
                </div>
                {phases.map((item, index) => {
                  const state = phaseState(index, phase)
                  const expanded = state === "current" && !settled
                  const parts = step({ leaving: !expanded })
                  return (
                    <span
                      key={item.id}
                      data-testid="phase-dot"
                      data-state={state}
                      className={dot({ reached: state === "current" || state === "done" })}
                      style={expanded ? undefined : { viewTransitionName: phaseName(index) }}
                    >
                      {(expanded || (settling && index === phase)) && (
                        <span aria-hidden data-testid={expanded ? "phase-step" : "phase-badge"} className={parts.root()}>
                          <span
                            data-step-circle
                            className={parts.circle()}
                            style={expanded ? { viewTransitionName: phaseName(index) } : undefined}
                            onAnimationEnd={(event) => {
                              if (event.target === event.currentTarget && event.animationName === "onboarding-phase-settle") setSettling(false)
                            }}
                          >
                            {item.icon}
                          </span>
                          <span data-step-label className={parts.label()} style={expanded ? { viewTransitionName: `${phaseName(index)}-label` } : undefined}>
                            {item.label}
                          </span>
                        </span>
                      )}
                    </span>
                  )
                })}
              </>
            )}
          </div>
        </div>
      )}
      {showInfo && <PhasesSheet open={open} title={phasesTitle} phases={phases} current={current} fraction={fraction} onClose={() => setOpen(false)} />}
    </header>
  )
}
